import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { RangePicker } from "../components/RangePicker";
import { Card, ErrorState, Loading } from "../components/ui";
import { api } from "../lib/api";
import { useDemo } from "../lib/demo";
import { downloadFile } from "../lib/download";
import { LABELS } from "../lib/format";
import type { ExportDataset, ProductBrief } from "../lib/types";

/**
 * Eksport surowych rekordów do CSV — całego portfela albo wybranych produktów.
 *
 * Plik zawiera rekordy, nie metryki. Odpadających klientów daje zbiór ruchów
 * MRR z rodzajem `churn` albo klienci w stanie `churned`, a nie trzecia
 * definicja churnu policzona w arkuszu.
 */

const HINTS: Record<string, string> = {
  customers: "Konta klientów: stan, daty rejestracji, triala, konwersji i odejścia, źródło pozyskania.",
  users: "Rejestracje osób: kiedy się zarejestrowały, aktywowały i ostatnio zalogowały.",
  subscriptions: "Subskrypcje: plan, cena, MRR i daty — od startu po wygaśnięcie.",
  payments: "Płatności z CRM-ów: udane, nieudane i zwrócone, z kwotą w walucie raportowej.",
  movements: "Każda zmiana MRR: nowi klienci, rozwój, obniżki, odejścia i powroty.",
  events: "Surowe zdarzenia z produktów, z treścią i stanem przetwarzania.",
};

const FILTER_LABELS: Record<string, string> = {
  status: "Stan",
  kind: "Rodzaj ruchu",
  event_name: "Nazwa zdarzenia",
  only_errors: "Tylko z błędem",
};

const VALUE_LABELS: Record<string, string> = {
  ...LABELS.customerStatus,
  ...LABELS.movement,
  paused: "wstrzymana",
  expired: "wygasła",
  success: "udane",
  failed: "nieudane",
  refunded: "zwrócone",
  deleted: "usunięte",
};

export function ExportPage() {
  const { scoped, key } = useDemo();
  const datasets = useQuery({
    queryKey: ["export", "zbiory"],
    queryFn: () => api.get<{ datasets: ExportDataset[]; timezone: string }>("/export"),
  });
  const products = useQuery({
    queryKey: ["products", "wszystkie"],
    queryFn: () => api.get<{ products: ProductBrief[] }>("/products"),
  });

  const [dataset, setDataset] = useState("payments");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [whole, setWhole] = useState(true);
  const [range, setRange] = useState("30d");
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [state, setState] = useState<{ busy: boolean; done?: string; error?: unknown }>({
    busy: false,
  });

  if (datasets.isPending || products.isPending) return <Loading />;
  if (datasets.isError) return <ErrorState error={datasets.error} />;
  if (products.isError) return <ErrorState error={products.error} />;

  const source = datasets.data.datasets.find((item) => item.key === dataset);
  const available = products.data.products.filter((product) => product.status !== "archived");

  const params = new URLSearchParams();
  if (picked.size) params.set("products", [...picked].join(","));
  if (!whole) params.set("range", range);
  for (const name of Object.keys(source?.filters ?? {})) {
    if (filters[name]) params.set(name, filters[name]!);
  }
  const query = params.toString();
  const path = scoped(`/export/${dataset}${query ? `?${query}` : ""}`);

  const download = async () => {
    setState({ busy: true });
    try {
      const filename = await downloadFile(path);
      setState({ busy: false, done: filename });
    } catch (error) {
      setState({ busy: false, error });
    }
  };

  const toggle = (slug: string) => {
    const next = new Set(picked);
    if (next.has(slug)) next.delete(slug);
    else next.add(slug);
    setPicked(next);
  };

  return (
    <>
      <header className="page-head">
        <div>
          <p className="page-head__eyebrow">Portfolio</p>
          <h1>Eksport danych</h1>
          <p className="page-head__sub">
            Plik CSV dla Excela: średnik jako separator, kwoty w groszach i jako tekst „49,00”,
            czas w strefie {datasets.data.timezone}.
          </p>
        </div>
      </header>

      <div className="grid grid--2">
        <Card title="Co eksportować">
          <div className="export-sets" role="radiogroup" aria-label="Zbiór danych">
            {datasets.data.datasets.map((item) => (
              <label key={item.key} className="export-set">
                <input
                  type="radio"
                  name="zbior"
                  value={item.key}
                  checked={dataset === item.key}
                  onChange={() => {
                    setDataset(item.key);
                    setFilters({});
                    setState({ busy: false });
                  }}
                />
                <span>
                  <b>{item.label}</b>
                  <small>{HINTS[item.key]}</small>
                </span>
              </label>
            ))}
          </div>
        </Card>

        <Card title="Filtry">
          <div className="form-grid">
            <fieldset className="form-grid__wide">
              <legend>Produkty</legend>
              <p className="form-note">Bez zaznaczenia — cały portfel{key === "bez-demo" ? " bez danych demo" : ""}.</p>
              {available.map((product) => (
                <label key={product.slug} className="checkbox">
                  <input
                    type="checkbox"
                    checked={picked.has(product.slug)}
                    onChange={() => toggle(product.slug)}
                  />
                  <span>
                    {product.name}
                    {product.is_demo && <span className="navlink__tag">demo</span>}
                  </span>
                </label>
              ))}
            </fieldset>

            <fieldset className="form-grid__wide">
              <legend>Okres</legend>
              <label className="checkbox">
                <input type="checkbox" checked={whole} onChange={(e) => setWhole(e.target.checked)} />
                <span>Cała historia</span>
              </label>
              {!whole && (
                <div className="export-range">
                  <RangePicker value={range} onChange={setRange} />
                  <small>
                    {dataset === "movements"
                      ? "Po dniu ruchu MRR."
                      : dataset === "customers" || dataset === "users"
                        ? "Po dniu rejestracji."
                        : dataset === "subscriptions"
                          ? "Po dniu startu subskrypcji."
                          : "Po dniu wystąpienia."}
                  </small>
                </div>
              )}
            </fieldset>

            {Object.entries(source?.filters ?? {}).map(([name, values]) => (
              <label key={name}>
                <span>{FILTER_LABELS[name] ?? name}</span>
                {name === "only_errors" ? (
                  <select
                    value={filters[name] ?? ""}
                    onChange={(e) => setFilters({ ...filters, [name]: e.target.value })}
                  >
                    <option value="">wszystkie</option>
                    <option value="1">tylko z błędem</option>
                  </select>
                ) : values.length ? (
                  <select
                    value={filters[name] ?? ""}
                    onChange={(e) => setFilters({ ...filters, [name]: e.target.value })}
                  >
                    <option value="">wszystkie</option>
                    {values.map((value) => (
                      <option key={value} value={value}>
                        {VALUE_LABELS[value] ?? value}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    value={filters[name] ?? ""}
                    placeholder="np. PAYMENT_SUCCESS"
                    onChange={(e) => setFilters({ ...filters, [name]: e.target.value.trim().toUpperCase() })}
                  />
                )}
              </label>
            ))}
          </div>

          <div className="export-foot">
            <button type="button" className="btn btn--primary" disabled={state.busy} onClick={download}>
              {state.busy ? "Przygotowuję plik…" : "Pobierz CSV"}
            </button>
            {state.done && <span className="dim">Pobrano {state.done}.</span>}
          </div>
          {state.error !== undefined && (
            <p className="form-error">
              {state.error instanceof Error ? state.error.message : "Nie udało się pobrać pliku."}
            </p>
          )}
          {source && (
            <p className="form-note">
              Kolumny: <code>{source.columns.join(", ")}</code>
            </p>
          )}
        </Card>
      </div>
    </>
  );
}
